import { fetchAuthSession } from 'aws-amplify/auth';
import { apiConfig } from '../amplify-config';

export * from './types';
import type {
  WorkspaceListResponse,
  WorkspaceFileResponse,
  VisualizeResponse,
  NutritionHistoryResponse,
  UserProfile,
  LinkPhoneResponse,
  WorkspaceExportResponse,
  McpToken,
  MintedMcpToken,
  Chat,
  ChatsListResponse,
  WaitlistSignupResponse,
  PublicStartSession,
  ClaimPublicSessionRequest,
  ClaimPublicSessionResponse,
  BillingStatus,
  CheckoutSession,
  PortalSession,
  CoachingPackage,
  PackagePurchase,
  ChatMessage,
  ArtifactUpdate,
  ChatArtifact,
  ActivityUpdate,
  TrainingWorkout,
  Weekday,
  TrainingWeek,
  BasePlanEnvelope,
  TrainingPlan,
  MealPlan,
  MealPlanProfile,
  FitnessProfile,
  CoachVisibilityPolicy,
  CoachProfile,
  CoachLocation,
  AvailabilityEntry,
  ScheduleSlot,
  ScheduleBooking,
  SchedulingDashboard,
  PublicBookingPage,
  TrainerAssignment,
  ActiveCoach,
  DmMessage,
  DmThread,
  DmThreadListItem,
  TrainerRosterResponse,
  TrainerIncomingResponse,
  TrainerClientSummary,
  ModelProviderStatus,
  ChatGPTDeviceStart,
  ChatGPTDevicePoll,
} from './types';

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function resolveFreshToken(fallback: string): Promise<string> {
  try {
    const session = await fetchAuthSession();
    const freshToken = session.tokens?.idToken?.toString();
    if (freshToken) {
      return freshToken;
    }

    console.warn('Unable to resolve a fresh auth token; using the provided fallback token.');
  } catch (error) {
    console.warn('Unable to refresh the auth session; using the provided fallback token.', error);
  }

  return fallback;
}

/**
 * @param token Deprecated fallback only. Requests resolve a fresh Amplify ID token first.
 */
async function fetchWithAuth<T>(
  url: string,
  token: string,
  options: RequestInit = {}
): Promise<T> {
  const authToken = await resolveFreshToken(token);
  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(error.error || `HTTP ${response.status}`, response.status);
  }

  return response.json();
}

function mcpApiUrl(path: string): string {
  return new URL(path.replace(/^\/+/, ''), apiConfig.mcpServerUrl).toString();
}

/**
 * The MCP Function URL is a distinct authenticated service, not an API Gateway route.
 * It uses the same short-lived Cognito ID token to mint/list/revoke a user's PATs.
 */
async function fetchMcpWithAuth<T>(
  path: string,
  token: string,
  options: RequestInit = {},
): Promise<T> {
  const authToken = await resolveFreshToken(token);
  const response = await fetch(mcpApiUrl(path), {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(error.error || `HTTP ${response.status}`, response.status);
  }

  return response.json() as Promise<T>;
}

export async function listMcpTokens(token: string): Promise<McpToken[]> {
  const response = await fetchMcpWithAuth<{ tokens?: McpToken[] }>('tokens', token);
  return Array.isArray(response.tokens) ? response.tokens : [];
}

export async function mintMcpToken(token: string, label: string): Promise<MintedMcpToken> {
  return fetchMcpWithAuth<MintedMcpToken>('tokens', token, {
    method: 'POST',
    body: JSON.stringify({ label }),
  });
}

export async function revokeMcpToken(token: string, tokenHash: string): Promise<void> {
  await fetchMcpWithAuth<{ ok: boolean }>('tokens/revoke', token, {
    method: 'POST',
    body: JSON.stringify({ tokenHash }),
  });
}

/**
 * List files and folders in workspace
 */
export async function listWorkspace(
  token: string,
  path: string = ''
): Promise<WorkspaceListResponse> {
  const url = path
    ? `${apiConfig.restApiUrl}api/workspace/${encodeURIComponent(path)}`
    : `${apiConfig.restApiUrl}api/workspace`;

  return fetchWithAuth<WorkspaceListResponse>(url, token);
}

/**
 * Read file content from workspace
 */
export async function readFile(
  token: string,
  path: string
): Promise<WorkspaceFileResponse> {
  const url = `${apiConfig.restApiUrl}api/workspace/${encodeURIComponent(path)}`;
  return fetchWithAuth<WorkspaceFileResponse>(url, token);
}

export async function getNutritionHistory(token: string, days = 30): Promise<NutritionHistoryResponse> {
  const url = `${apiConfig.restApiUrl}api/nutrition/history?days=${days}`;
  return fetchWithAuth<NutritionHistoryResponse>(url, token);
}

/**
 * Write UTF-8 text content back to a canonical workspace file.
 */
export async function writeWorkspaceFile(
  token: string,
  path: string,
  content: string,
  contentType?: string
): Promise<WorkspaceFileResponse> {
  const url = `${apiConfig.restApiUrl}api/workspace/${encodeURIComponent(path)}`;
  return fetchWithAuth<WorkspaceFileResponse>(url, token, {
    method: 'PUT',
    body: JSON.stringify({ content, contentType }),
  });
}

/**
 * Generate visualization from query
 */
export async function visualize(
  token: string,
  query: string
): Promise<VisualizeResponse> {
  const url = `${apiConfig.restApiUrl}api/visualize`;
  return fetchWithAuth<VisualizeResponse>(url, token, {
    method: 'POST',
    body: JSON.stringify({ query }),
  });
}

/**
 * Get user profile
 */
export async function getProfile(token: string): Promise<UserProfile> {
  const url = `${apiConfig.restApiUrl}api/profile`;
  return fetchWithAuth<UserProfile>(url, token);
}

export async function updateTimezone(
  token: string,
  timezone: string,
): Promise<{ success: boolean; timezone: string }> {
  const url = `${apiConfig.restApiUrl}api/profile/timezone`;
  return fetchWithAuth<{ success: boolean; timezone: string }>(url, token, {
    method: 'PUT',
    body: JSON.stringify({ timezone }),
  });
}

export async function exportWorkspace(token: string): Promise<WorkspaceExportResponse> {
  const url = `${apiConfig.restApiUrl}api/profile/export`;
  return fetchWithAuth<WorkspaceExportResponse>(url, token, {
    method: 'POST',
  });
}

export async function deleteAccount(token: string): Promise<{ deleted: boolean }> {
  const url = `${apiConfig.restApiUrl}api/profile/account`;
  return fetchWithAuth<{ deleted: boolean }>(url, token, {
    method: 'DELETE',
    body: JSON.stringify({ confirm: 'DELETE' }),
  });
}

/**
 * Link phone number to user account
 */
export async function linkPhone(
  token: string,
  phone: string
): Promise<LinkPhoneResponse> {
  const url = `${apiConfig.restApiUrl}api/profile/link-phone`;
  return fetchWithAuth<LinkPhoneResponse>(url, token, {
    method: 'POST',
    body: JSON.stringify({ phone }),
  });
}

/**
 * Verify phone OTP and complete linking
 */
export async function verifyPhone(
  token: string,
  otp: string
): Promise<LinkPhoneResponse> {
  const url = `${apiConfig.restApiUrl}api/profile/verify-phone`;
  return fetchWithAuth<LinkPhoneResponse>(url, token, {
    method: 'POST',
    body: JSON.stringify({ otp }),
  });
}

/**
 * Unlink phone number from user account
 */
export async function unlinkPhone(token: string): Promise<{ success: boolean; message: string }> {
  const url = `${apiConfig.restApiUrl}api/profile/unlink-phone`;
  return fetchWithAuth<{ success: boolean; message: string }>(url, token, {
    method: 'POST',
  });
}

/**
 * Fetch the VAPID public key needed to create a Web Push subscription.
 */
export async function getPushConfig(token: string): Promise<{ vapidPublicKey: string }> {
  const url = `${apiConfig.restApiUrl}api/profile/push-config`;
  return fetchWithAuth<{ vapidPublicKey: string }>(url, token);
}

/**
 * Register a Web Push subscription for the current user.
 */
export async function upsertPushSubscription(
  token: string,
  subscription: { endpoint: string; p256dh: string; auth: string }
): Promise<{ success: boolean; subscriptionCount: number }> {
  const url = `${apiConfig.restApiUrl}api/profile/push-subscription`;
  return fetchWithAuth<{ success: boolean; subscriptionCount: number }>(url, token, {
    method: 'POST',
    body: JSON.stringify(subscription),
  });
}

/**
 * Remove a Web Push subscription by its endpoint URL.
 */
export async function deletePushSubscription(
  token: string,
  endpoint: string
): Promise<{ success: boolean; subscriptionCount: number }> {
  const url = `${apiConfig.restApiUrl}api/profile/push-subscription`;
  return fetchWithAuth<{ success: boolean; subscriptionCount: number }>(url, token, {
    method: 'DELETE',
    body: JSON.stringify({ endpoint }),
  });
}

/**
 * Opt the user in/out of proactive coach check-ins (default off).
 */
export async function updateProactiveSettings(
  token: string,
  enabled: boolean,
): Promise<{ success: boolean; proactive: { enabled: boolean } }> {
  const url = `${apiConfig.restApiUrl}api/profile/settings/proactive`;
  return fetchWithAuth<{ success: boolean; proactive: { enabled: boolean } }>(url, token, {
    method: 'POST',
    body: JSON.stringify({ enabled }),
  });
}

/**
 * List all chats for the current user
 */
export async function listChats(token: string): Promise<Chat[]> {
  const url = `${apiConfig.restApiUrl}api/chats`;
  const response = await fetchWithAuth<ChatsListResponse>(url, token);
  return response.chats;
}

/**
 * Create a new chat. Pass `coachClientId` to create a coach-assistant chat
 * scoped to that client (the server verifies the assignment per turn).
 */
export async function createChat(token: string, title?: string, coachClientId?: string): Promise<Chat> {
  const url = `${apiConfig.restApiUrl}api/chats`;
  return fetchWithAuth<Chat>(url, token, {
    method: 'POST',
    body: JSON.stringify({ title, coachClientId }),
  });
}

export interface ChatBootstrapResponse {
  chat: Chat | null;
  messages: ChatMessage[];
  sourceUsed: 'cold_start' | 'existing_bootstrap';
  bootstrapped: boolean;
}

/**
 * First-run bootstrap: creates (or resumes) the user's Getting Started chat with
 * the coach's intake opener. Runs the agent synchronously — can take ~10-30s.
 */
export async function bootstrapChat(token: string): Promise<ChatBootstrapResponse> {
  const url = `${apiConfig.restApiUrl}api/chats/bootstrap`;
  return fetchWithAuth<ChatBootstrapResponse>(url, token, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/**
 * Update a chat (title)
 */
export async function updateChat(token: string, chatId: string, title: string): Promise<Chat> {
  const url = `${apiConfig.restApiUrl}api/chats/${encodeURIComponent(chatId)}`;
  return fetchWithAuth<Chat>(url, token, {
    method: 'PUT',
    body: JSON.stringify({ title }),
  });
}

/**
 * Delete a chat
 */
export async function deleteChat(token: string, chatId: string): Promise<void> {
  const url = `${apiConfig.restApiUrl}api/chats/${encodeURIComponent(chatId)}`;
  await fetchWithAuth<{ deleted: boolean }>(url, token, {
    method: 'DELETE',
  });
}

export async function submitWaitlistSignup(
  email: string,
  publicSessionId?: string,
): Promise<WaitlistSignupResponse> {
  const url = `${apiConfig.restApiUrl}api/waitlist`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email,
      publicSessionId,
    }),
  });

  const data = await response.json().catch(() => ({ error: 'Failed to join waitlist' }));
  if (!response.ok) {
    throw new Error(data.error || 'Failed to join waitlist');
  }

  return data as WaitlistSignupResponse;
}

/**
 * Error from the public /start REST API that carries the HTTP status, so
 * callers can distinguish "this session id is definitively invalid" (4xx)
 * from transient failures (network error / 5xx / 429) that must NOT trigger
 * a fresh session mint (twa-7lj: repeated mints burn the per-IP limit).
 */
export class PublicStartApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'PublicStartApiError';
    this.status = status;
  }
}

export async function createPublicStartSession(existingSessionId?: string): Promise<PublicStartSession> {
  const url = `${apiConfig.restApiUrl}public/start/session`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    // Idempotency hint: if this browser already holds a live session (e.g. a
    // concurrent tab just minted one), the server returns it instead of
    // minting again — and without charging the per-IP mint throttle.
    body: existingSessionId ? JSON.stringify({ existingSessionId }) : undefined,
  });

  const data = await response.json().catch(() => ({ error: 'Failed to start public session' }));
  if (!response.ok) {
    throw new PublicStartApiError(data.error || 'Failed to start public session', response.status);
  }

  return data.session as PublicStartSession;
}

export async function getPublicStartSession(sessionId: string): Promise<PublicStartSession> {
  const url = `${apiConfig.restApiUrl}public/start/session/${encodeURIComponent(sessionId)}`;
  const response = await fetch(url);

  const data = await response.json().catch(() => ({ error: 'Failed to load public session' }));
  if (!response.ok) {
    throw new PublicStartApiError(data.error || 'Failed to load public session', response.status);
  }

  return data.session as PublicStartSession;
}

export async function claimPublicStartSession(
  token: string,
  request: ClaimPublicSessionRequest,
  signal?: AbortSignal,
): Promise<ClaimPublicSessionResponse> {
  const url = `${apiConfig.restApiUrl}api/chats/claim-public-session`;
  return fetchWithAuth<ClaimPublicSessionResponse>(url, token, {
    method: 'POST',
    body: JSON.stringify(request),
    signal,
  });
}

/**
 * Get billing status and usage
 */
export async function getBillingStatus(token: string): Promise<BillingStatus> {
  const url = `${apiConfig.restApiUrl}api/billing/status`;
  return fetchWithAuth<BillingStatus>(url, token);
}

/**
 * Create Stripe checkout session
 */
export async function createCheckoutSession(
  token: string,
  tier: string,
  successUrl: string,
  cancelUrl: string
): Promise<CheckoutSession> {
  const url = `${apiConfig.restApiUrl}api/billing/checkout`;
  return fetchWithAuth<CheckoutSession>(url, token, {
    method: 'POST',
    body: JSON.stringify({ tier, successUrl, cancelUrl }),
  });
}

/**
 * Create Stripe customer portal session
 */
export async function createPortalSession(
  token: string,
  returnUrl: string
): Promise<PortalSession> {
  const url = `${apiConfig.restApiUrl}api/billing/portal`;
  return fetchWithAuth<PortalSession>(url, token, {
    method: 'POST',
    body: JSON.stringify({ returnUrl }),
  });
}

export async function listCoachPackages(token: string, coachId: string): Promise<CoachingPackage[]> {
  const url = `${apiConfig.restApiUrl}api/billing/packages?coachId=${encodeURIComponent(coachId)}`;
  const response = await fetchWithAuth<{ packages: CoachingPackage[] }>(url, token);
  return response.packages;
}

export async function listPackagePurchases(token: string): Promise<PackagePurchase[]> {
  const url = `${apiConfig.restApiUrl}api/billing/packages/purchases`;
  const response = await fetchWithAuth<{ purchases: PackagePurchase[] }>(url, token);
  return response.purchases;
}

export async function createCoachPackageCheckoutSession(
  token: string,
  coachId: string,
  packageId: string,
  successUrl: string,
  cancelUrl: string
): Promise<CheckoutSession> {
  const url = `${apiConfig.restApiUrl}api/billing/packages/checkout`;
  return fetchWithAuth<CheckoutSession>(url, token, {
    method: 'POST',
    body: JSON.stringify({ coachId, packageId, successUrl, cancelUrl }),
  });
}

interface SendMessageOptions {
  hidden?: boolean;
}

export type TokenProvider = () => Promise<string>;

/**
 * Thrown by getHistory when the chat doesn't exist for this user (HTTP 404).
 * Lets callers detect a stale chatId from localStorage and recover.
 */
export class ChatNotFoundError extends Error {
  constructor(public readonly chatId: string) {
    super(`Chat not found: ${chatId}`);
    this.name = 'ChatNotFoundError';
  }
}

/**
 * SSE client for chat - replaces WebSocket with simpler HTTP streaming
 */
export class ChatSSE {
  private getToken: TokenProvider;
  private abortController: AbortController | null = null;
  private currentChatId: string | null = null;

  onMessage: ((message: ChatMessage) => void) | null = null;
  onActivity: ((activity: ActivityUpdate) => void) | null = null;
  onToken: ((text: string) => void) | null = null;
  onError: ((error: string) => void) | null = null;
  onDone: (() => void) | null = null;
  // Fired when the stream closed without delivering any payload (no token,
  // no message, no activity, no explicit error). The user's message has
  // effectively vanished — caller should surface a recovery error rather
  // than leave the UI silent.
  onSilentDrop: (() => void) | null = null;

  constructor(tokenSource: string | TokenProvider) {
    this.getToken = typeof tokenSource === 'function'
      ? tokenSource
      : async () => tokenSource;
  }

  setTokenProvider(provider: TokenProvider): void {
    this.getToken = provider;
  }

  setCurrentChat(chatId: string | null): void {
    this.currentChatId = chatId;
  }

  getCurrentChatId(): string | null {
    return this.currentChatId;
  }

  async sendMessage(message: string, chatId?: string, options: SendMessageOptions = {}): Promise<void> {
    const abortController = new AbortController();
    this.abortController = abortController;
    const targetChatId = chatId || this.currentChatId;
    let doneNotified = false;
    let receivedAnyEvent = false;

    if (!targetChatId) {
      this.onError?.('No chat selected');
      return;
    }

    let token: string;
    try {
      token = await this.getToken();
    } catch (err) {
      this.onError?.(err instanceof Error ? `Auth failed: ${err.message}` : 'Auth failed');
      return;
    }
    if (!token) {
      this.onError?.('Not signed in');
      return;
    }

    try {
      const response = await fetch(apiConfig.chatSSEUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          message,
          chatId: targetChatId,
          hidden: options.hidden ?? false,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          clientSource: 'web',
        }),
        signal: abortController.signal,
      });

      if (!response.ok) {
        let errorMsg = `Request failed: ${response.status}`;
        try {
          const error = await response.json();
          errorMsg = error.error || errorMsg;
        } catch {
          // Ignore JSON parse error
        }
        this.onError?.(errorMsg);
        return;
      }

      if (!response.body) {
        this.onError?.('No response body');
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Split on double newlines (SSE event separator)
        const events = buffer.split('\n\n');
        // Keep the last potentially incomplete event in the buffer
        buffer = events.pop() || '';

        for (const eventBlock of events) {
          if (!eventBlock.trim()) continue;

          const parsed = this.parseSSEBlock(eventBlock);
          if (parsed) {
            receivedAnyEvent = true;
            const isDone = this.handleSSEEvent(parsed.type, parsed.data);
            if (isDone) doneNotified = true;
          }
        }
      }

      // Process any remaining data in buffer
      if (buffer.trim()) {
        const parsed = this.parseSSEBlock(buffer);
        if (parsed) {
          receivedAnyEvent = true;
          const isDone = this.handleSSEEvent(parsed.type, parsed.data);
          if (isDone) doneNotified = true;
        }
      }

      // If the stream closed without an explicit done event, still terminate UI state.
      if (!doneNotified) {
        this.onDone?.();
      }

      // Stream closed with HTTP 200 but delivered nothing — agent never wrote
      // anything, persistence didn't happen, and recovering via syncHistory
      // would silently swallow the failure. Surface it explicitly.
      if (!receivedAnyEvent) {
        this.onSilentDrop?.();
      }

    } catch (err) {
      // Browsers disagree on what an aborted fetch throws: Chrome/Firefox raise
      // a DOMException named "AbortError", but Safari (notably iOS) often throws
      // "TypeError: Load failed" during a streaming read. Trust the signal state
      // — we initiated the abort, so suppress the error regardless of how the
      // browser surfaced it.
      if (abortController.signal.aborted) {
        return;
      }
      if (err instanceof Error) {
        this.onError?.(err.message);
      } else {
        this.onError?.('Unknown error occurred');
      }
    }
  }

  private parseSSEBlock(block: string): { type: string; data: unknown } | null {
    const lines = block.split('\n');
    let eventType = '';
    let data = '';

    for (const line of lines) {
      if (line.startsWith('event: ')) {
        eventType = line.slice(7).trim();
      } else if (line.startsWith('data: ')) {
        data = line.slice(6);
      }
    }

    if (eventType && data) {
      try {
        return { type: eventType, data: JSON.parse(data) };
      } catch {
        console.error('Failed to parse SSE data:', data);
        return null;
      }
    }
    return null;
  }

  private handleSSEEvent(type: string, data: unknown): boolean {
    const eventData = data as Record<string, unknown>;

    switch (type) {
      case 'token':
        // Real-time token streaming from the AI
        this.onToken?.(eventData.text as string);
        return false;

      case 'activity':
        this.onActivity?.({
          activity: eventData.activity as ActivityUpdate['activity'],
          detail: eventData.detail as string | undefined,
        });
        return false;

      case 'message':
        // NOTE(twa-oj6): artifact payloads only arrive here, on the final
        // `message` event at turn end. A dedicated `artifact` SSE event
        // (streamed as soon as the runtime builds a card mid-turn) is the
        // planned follow-up; until then the UI infers skeleton placeholders
        // from `activity` events.
        this.onMessage?.({
          content: eventData.content as string,
          timestamp: eventData.timestamp as number,
          role: 'assistant',
          chatId: eventData.chatId as string,
          persisted: eventData.persisted as boolean,
          artifacts: (eventData.artifacts as ChatArtifact[] | undefined) || [],
          hidden: eventData.hidden as boolean | undefined,
          artifactUpdate: eventData.artifactUpdate as ArtifactUpdate | undefined,
        });
        return false;

      case 'error':
        this.onError?.(eventData.error as string);
        return false;

      case 'done':
        // Stream completed
        this.onDone?.();
        return true;

      default:
        console.log('Unknown SSE event type:', type);
        return false;
    }
  }

  cancel(): void {
    this.abortController?.abort();
    this.abortController = null;
  }

  /**
   * Fetch chat history via REST API
   * (SSE doesn't maintain a connection, so history is fetched separately)
   *
   * Throws ChatNotFoundError on 404 so the caller can recover from a stale
   * chatId in localStorage. Other errors return an empty list (caller keeps
   * its current state).
   */
  async getHistory(chatId: string): Promise<ChatMessage[]> {
    let token: string;
    try {
      token = await this.getToken();
    } catch (err) {
      console.error('Failed to fetch token for chat history:', err);
      return [];
    }

    try {
      const url = `${apiConfig.restApiUrl}api/chats/${encodeURIComponent(chatId)}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 404) {
        throw new ChatNotFoundError(chatId);
      }

      if (!response.ok) {
        console.error('Failed to fetch chat history:', response.status);
        return [];
      }

      const data = await response.json();
      return data.messages || [];
    } catch (err) {
      if (err instanceof ChatNotFoundError) {
        throw err;
      }
      console.error('Error fetching chat history:', err);
      return [];
    }
  }
}

export class PublicStartSSE {
  private abortController: AbortController | null = null;

  onMessage: ((message: ChatMessage) => void) | null = null;
  onActivity: ((activity: ActivityUpdate) => void) | null = null;
  onToken: ((text: string) => void) | null = null;
  onState: ((session: PublicStartSession) => void) | null = null;
  onError: ((error: string) => void) | null = null;
  onDone: (() => void) | null = null;

  async sendMessage(message: string, sessionId: string): Promise<void> {
    const abortController = new AbortController();
    this.abortController = abortController;
    let doneNotified = false;

    try {
      if (apiConfig.publicStartSSEUrl.includes('example.invalid')) {
        this.onError?.('The public chat endpoint is not configured for this build yet.');
        return;
      }

      const response = await fetch(apiConfig.publicStartSSEUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message, sessionId }),
        signal: abortController.signal,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: `Request failed: ${response.status}` }));
        if (error.session) {
          this.onState?.(error.session as PublicStartSession);
        }
        this.onError?.(error.error || `Request failed: ${response.status}`);
        return;
      }

      if (!response.body) {
        this.onError?.('No response body');
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const eventBlock of events) {
          const parsed = this.parseSSEBlock(eventBlock);
          if (!parsed) {
            continue;
          }
          const isDone = this.handleSSEEvent(parsed.type, parsed.data);
          if (isDone) {
            doneNotified = true;
          }
        }
      }

      if (buffer.trim()) {
        const parsed = this.parseSSEBlock(buffer);
        if (parsed) {
          const isDone = this.handleSSEEvent(parsed.type, parsed.data);
          if (isDone) {
            doneNotified = true;
          }
        }
      }

      if (!doneNotified) {
        this.onDone?.();
      }
    } catch (err) {
      // See ChatSSE.sendMessage: trust the signal, not the error name —
      // Safari throws "TypeError: Load failed" on aborted streams.
      if (abortController.signal.aborted) {
        return;
      }
      this.onError?.(err instanceof Error ? err.message : 'Unknown error occurred');
    }
  }

  private parseSSEBlock(block: string): { type: string; data: unknown } | null {
    const lines = block.split('\n');
    let eventType = '';
    let data = '';

    for (const line of lines) {
      if (line.startsWith('event: ')) {
        eventType = line.slice(7).trim();
      } else if (line.startsWith('data: ')) {
        data = line.slice(6);
      }
    }

    if (!eventType || !data) {
      return null;
    }

    try {
      return { type: eventType, data: JSON.parse(data) };
    } catch {
      console.error('Failed to parse public start SSE data:', data);
      return null;
    }
  }

  private handleSSEEvent(type: string, data: unknown): boolean {
    const eventData = data as Record<string, unknown>;

    switch (type) {
      case 'token':
        this.onToken?.(eventData.text as string);
        return false;

      case 'activity':
        this.onActivity?.({
          activity: eventData.activity as ActivityUpdate['activity'],
          detail: eventData.detail as string | undefined,
        });
        return false;

      case 'message':
        this.onMessage?.({
          content: eventData.content as string,
          timestamp: eventData.timestamp as number,
          role: eventData.role as ChatMessage['role'],
          chatId: eventData.chatId as string | undefined,
          persisted: eventData.persisted as boolean | undefined,
        });
        return false;

      case 'state':
        this.onState?.(eventData.session as PublicStartSession);
        return false;

      case 'error':
        this.onError?.(eventData.error as string);
        return false;

      case 'done':
        this.onDone?.();
        return true;

      default:
        return false;
    }
  }

  cancel(): void {
    this.abortController?.abort();
    this.abortController = null;
  }
}

// ========================================
// Training Plan Types (JSON Schema)
// ========================================

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const WEEKDAY_ORDER: readonly Weekday[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export function getTrainingWeek(plan: TrainingPlan, weekNumber: number): TrainingWeek | null {
  return plan.weeks.find((week) => week.weekNumber === weekNumber) || null;
}

export function parseDateOnly(value: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return new Date(value);
  }

  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day));
}

export function toIsoDay(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function diffLocalCalendarDays(start: Date, end: Date): number {
  const startUtc = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const endUtc = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.floor((endUtc - startUtc) / MS_PER_DAY);
}

// Convert a Date to the lowercase weekday name used as keys in
// ``TrainingWeek.workouts`` (e.g. ``"tuesday"``).
export function weekdayName(date: Date): Weekday {
  // JS getDay(): 0 = Sunday. Our keys are Monday-first.
  const map: Weekday[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return map[date.getDay()];
}

export function getCurrentTrainingWeek(plan: TrainingPlan, targetDate: Date = new Date()): number {
  const start = parseDateOnly(plan.startDate);
  const raw = Math.floor(diffLocalCalendarDays(start, targetDate) / 7) + 1;
  return Math.max(1, Math.min(plan.durationWeeks, raw));
}

// Unlike getCurrentTrainingWeek (which clamps for navigation), this predicate
// reports whether the block is finished — the calendar week is past the last
// authored week. Used for the block-end CTA; not a navigation clamp.
export function isPlanCompleted(plan: TrainingPlan, targetDate: Date = new Date()): boolean {
  const start = parseDateOnly(plan.startDate);
  const rawWeek = Math.floor(diffLocalCalendarDays(start, targetDate) / 7) + 1;
  return rawWeek > plan.weeks.length;
}

// Resolve today's workout by:
//   1. Compute (target - startDate) // 7 → week index
//   2. Take target's weekday name → look up week.workouts[weekday]
// If the weekday isn't in plan.trainingDays, or the week's workouts
// dict has no entry for it, the day is a rest day → returns null.
export function getTrainingWorkoutForDate(plan: TrainingPlan, targetDate: Date): TrainingWorkout | null {
  const start = parseDateOnly(plan.startDate);
  const offsetDays = diffLocalCalendarDays(start, targetDate);
  if (offsetDays < 0) return null;
  const weekIndex = Math.floor(offsetDays / 7);
  if (weekIndex >= plan.weeks.length) return null;
  const week = plan.weeks[weekIndex];
  if (!week) return null;
  const weekday = weekdayName(targetDate);
  if (Array.isArray(plan.trainingDays) && plan.trainingDays.length > 0) {
    if (!plan.trainingDays.includes(weekday)) return null;
  }
  return week.workouts?.[weekday] ?? null;
}

export function getTodaysWorkout(plan: TrainingPlan): TrainingWorkout | null {
  return getTrainingWorkoutForDate(plan, new Date());
}

/**
 * Get training plan from workspace
 */
export async function getTrainingPlan(token: string): Promise<TrainingPlan | null> {
  try {
    const response = await readFile(token, 'data/training-plans/active-plan.json');
    if (response.content) {
      return JSON.parse(response.content);
    }
    return null;
  } catch {
    // Plan doesn't exist yet
    return null;
  }
}

/**
 * Flip the active training plan from 'pending_commit' to 'active'. Writes the updated
 * plan JSON back to the workspace. Returns the updated plan so callers can update state
 * without re-reading.
 */
export async function acceptTrainingPlan(token: string): Promise<TrainingPlan | null> {
  const plan = await getTrainingPlan(token);
  if (!plan) return null;
  if (plan.status !== 'pending_commit') return plan;
  const url = `${apiConfig.restApiUrl}api/plans/accept`;
  const response = await fetchWithAuth<{ plan: TrainingPlan }>(url, token, {
    method: 'POST',
    body: JSON.stringify({ planId: plan.planId }),
  });
  return response.plan;
}

/**
 * Get active meal plan from workspace
 */
export async function getMealPlan(token: string): Promise<MealPlan | null> {
  try {
    const response = await readFile(token, 'data/meal-plans/active-plan.json');
    if (response.content) {
      return JSON.parse(response.content);
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Get meal planning profile from workspace
 */
export async function getMealPlanProfile(token: string): Promise<MealPlanProfile | null> {
  try {
    const response = await readFile(token, 'data/meal-plans/profile.json');
    if (response.content) {
      return JSON.parse(response.content);
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Log a planned meal slot as eaten. The server recomputes the scaled nutrition,
 * appends the nutrition CSV row, and stamps the slot's loggingBridge — the
 * browser no longer writes data/meal-plans/ directly (twa-dvn.4).
 */
export async function logPlannedMealAsEaten(
  token: string,
  plan: MealPlan,
  dayDate: string,
  slotId: string,
  servings?: number
): Promise<{ nutritionPath: string; updatedPlan: MealPlan | null }> {
  const url = `${apiConfig.coachApiUrl}api/meal-plans/log-eaten`;
  return fetchWithAuth<{ nutritionPath: string; updatedPlan: MealPlan | null }>(url, token, {
    method: 'POST',
    body: JSON.stringify({ planId: plan.planId, dayDate, slotId, servings }),
  });
}

/**
 * Get fitness profile from workspace
 */
export async function getFitnessProfile(token: string): Promise<FitnessProfile | null> {
  try {
    const response = await readFile(token, 'data/profile/user.json');
    if (response.content) {
      return JSON.parse(response.content);
    }
    return null;
  } catch {
    // Profile doesn't exist yet
    return null;
  }
}

export async function getCoachVisibility(token: string): Promise<CoachVisibilityPolicy> {
  const url = `${apiConfig.restApiUrl}api/profile/coach-visibility`;
  const response = await fetchWithAuth<{ coachVisibility: CoachVisibilityPolicy }>(url, token);
  return response.coachVisibility;
}

export async function updateCoachVisibility(
  token: string,
  policy: Partial<CoachVisibilityPolicy>,
): Promise<CoachVisibilityPolicy> {
  const url = `${apiConfig.restApiUrl}api/profile/coach-visibility`;
  const response = await fetchWithAuth<{ coachVisibility: CoachVisibilityPolicy }>(url, token, {
    method: 'PUT',
    body: JSON.stringify(policy),
  });
  return response.coachVisibility;
}

async function fetchJson<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

export async function getSchedulingDashboard(token: string): Promise<SchedulingDashboard> {
  const url = `${apiConfig.coachApiUrl}api/scheduling/dashboard`;
  return fetchWithAuth<SchedulingDashboard>(url, token);
}

export async function updateCoachSchedulingProfile(token: string, profile: Partial<CoachProfile>): Promise<CoachProfile> {
  const url = `${apiConfig.coachApiUrl}api/scheduling/coach-profile`;
  const response = await fetchWithAuth<{ coachProfile: CoachProfile }>(url, token, {
    method: 'PUT',
    body: JSON.stringify(profile),
  });
  return response.coachProfile;
}

export async function createCoachLocation(
  token: string,
  location: Pick<CoachLocation, 'name' | 'address' | 'timezone' | 'publicIntroEnabled' | 'active'>
): Promise<CoachLocation> {
  const url = `${apiConfig.coachApiUrl}api/scheduling/locations`;
  const response = await fetchWithAuth<{ location: CoachLocation }>(url, token, {
    method: 'POST',
    body: JSON.stringify(location),
  });
  return response.location;
}

export async function updateCoachLocation(
  token: string,
  locationId: string,
  location: Partial<CoachLocation>
): Promise<CoachLocation> {
  const url = `${apiConfig.coachApiUrl}api/scheduling/locations/${encodeURIComponent(locationId)}`;
  const response = await fetchWithAuth<{ location: CoachLocation }>(url, token, {
    method: 'PUT',
    body: JSON.stringify(location),
  });
  return response.location;
}

export async function deleteCoachLocation(token: string, locationId: string): Promise<void> {
  const url = `${apiConfig.coachApiUrl}api/scheduling/locations/${encodeURIComponent(locationId)}`;
  await fetchWithAuth<{ deleted: boolean }>(url, token, { method: 'DELETE' });
}

export async function createAvailabilityEntry(
  token: string,
  entry: Partial<AvailabilityEntry> & { locationId: string; entryType: 'rule' | 'override' }
): Promise<AvailabilityEntry> {
  const url = `${apiConfig.coachApiUrl}api/scheduling/availability`;
  const response = await fetchWithAuth<{ availability: AvailabilityEntry }>(url, token, {
    method: 'POST',
    body: JSON.stringify(entry),
  });
  return response.availability;
}

export async function deleteAvailabilityEntry(token: string, availabilityId: string): Promise<void> {
  const url = `${apiConfig.coachApiUrl}api/scheduling/availability/${encodeURIComponent(availabilityId)}`;
  await fetchWithAuth<{ deleted: boolean }>(url, token, { method: 'DELETE' });
}

export async function listCoachLocationSlots(token: string, locationId: string, days: number = 14): Promise<ScheduleSlot[]> {
  const url = `${apiConfig.coachApiUrl}api/scheduling/locations/${encodeURIComponent(locationId)}/slots?days=${days}`;
  const response = await fetchWithAuth<{ slots: ScheduleSlot[] }>(url, token);
  return response.slots;
}

export async function cancelSchedulingBooking(token: string, bookingId: string): Promise<ScheduleBooking> {
  const url = `${apiConfig.coachApiUrl}api/scheduling/bookings/${encodeURIComponent(bookingId)}/cancel`;
  const response = await fetchWithAuth<{ booking: ScheduleBooking }>(url, token, { method: 'POST' });
  return response.booking;
}

export async function rescheduleSchedulingBooking(token: string, bookingId: string, startAt: string): Promise<ScheduleBooking> {
  const url = `${apiConfig.coachApiUrl}api/scheduling/bookings/${encodeURIComponent(bookingId)}/reschedule`;
  const response = await fetchWithAuth<{ booking: ScheduleBooking }>(url, token, {
    method: 'POST',
    body: JSON.stringify({ startAt }),
  });
  return response.booking;
}

export async function claimGuestScheduleBookings(token: string): Promise<ScheduleBooking[]> {
  const url = `${apiConfig.coachApiUrl}api/scheduling/claim-bookings`;
  const response = await fetchWithAuth<{ claimed: ScheduleBooking[] }>(url, token, { method: 'POST' });
  return response.claimed;
}

export async function getPublicBookingPage(publicSlug: string, days: number = 14): Promise<PublicBookingPage> {
  const url = `${apiConfig.restApiUrl}public/scheduling/locations/${encodeURIComponent(publicSlug)}?days=${days}`;
  return fetchJson<PublicBookingPage>(url);
}

export async function getPublicBookingSlots(publicSlug: string, days: number = 14): Promise<ScheduleSlot[]> {
  const url = `${apiConfig.restApiUrl}public/scheduling/locations/${encodeURIComponent(publicSlug)}/slots?days=${days}`;
  const response = await fetchJson<{ slots: ScheduleSlot[] }>(url);
  return response.slots;
}

export async function createPublicIntroBooking(
  publicSlug: string,
  payload: { name: string; email: string; phone: string; startAt: string }
): Promise<{ booking: ScheduleBooking; manageUrl: string; message: string }> {
  const url = `${apiConfig.restApiUrl}public/scheduling/locations/${encodeURIComponent(publicSlug)}/bookings`;
  return fetchJson<{ booking: ScheduleBooking; manageUrl: string; message: string }>(url, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function getGuestManagedBooking(magicToken: string): Promise<ScheduleBooking> {
  const url = `${apiConfig.restApiUrl}public/scheduling/manage/${encodeURIComponent(magicToken)}`;
  const response = await fetchJson<{ booking: ScheduleBooking }>(url);
  return response.booking;
}

export async function cancelGuestManagedBooking(magicToken: string): Promise<ScheduleBooking> {
  const url = `${apiConfig.restApiUrl}public/scheduling/manage/${encodeURIComponent(magicToken)}/cancel`;
  const response = await fetchJson<{ booking: ScheduleBooking }>(url, { method: 'POST' });
  return response.booking;
}

export async function rescheduleGuestManagedBooking(magicToken: string, startAt: string): Promise<ScheduleBooking> {
  const url = `${apiConfig.restApiUrl}public/scheduling/manage/${encodeURIComponent(magicToken)}/reschedule`;
  const response = await fetchJson<{ booking: ScheduleBooking }>(url, {
    method: 'POST',
    body: JSON.stringify({ startAt }),
  });
  return response.booking;
}

// ──────────────────────────────────────────────
// Trainer API — coach/client assignment management
// ──────────────────────────────────────────────

/** Coach sends an invite to a client by email. */
export async function sendTrainerInvite(token: string, email: string): Promise<void> {
  await fetchWithAuth(`${apiConfig.restApiUrl}api/trainer/invite`, token, {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

/**
 * Client accepts a pending invite via raw token (magic-link URL flow — user may not be logged in yet).
 * Pass the raw `token` query param from the invite URL.
 */
export async function acceptTrainerInviteByToken(token: string, inviteToken: string): Promise<void> {
  await fetchWithAuth(`${apiConfig.restApiUrl}api/trainer/invites/accept`, token, {
    method: 'POST',
    body: JSON.stringify({ token: inviteToken }),
  });
}

/**
 * Authenticated client accepts a pending invite by coachId (in-app flow).
 * Use this when the client is already logged in (e.g. from the ProfileSettings banner).
 */
export async function acceptTrainerInviteByCoachId(token: string, coachId: string): Promise<void> {
  await fetchWithAuth(
    `${apiConfig.restApiUrl}api/trainer/invites/${encodeURIComponent(coachId)}/accept`,
    token,
    { method: 'POST' },
  );
}

/**
 * Authenticated client declines a pending invite by coachId (in-app flow).
 */
export async function declineTrainerInviteByCoachId(token: string, coachId: string): Promise<void> {
  await fetchWithAuth(
    `${apiConfig.restApiUrl}api/trainer/invites/${encodeURIComponent(coachId)}/decline`,
    token,
    { method: 'POST' },
  );
}

/** Client declines a pending invite via raw token (magic-link flow). */
export async function declineTrainerInviteByToken(token: string, inviteToken: string): Promise<void> {
  await fetchWithAuth(`${apiConfig.restApiUrl}api/trainer/invites/decline`, token, {
    method: 'POST',
    body: JSON.stringify({ token: inviteToken }),
  });
}

// Backwards-compat aliases kept so callers that already exist can use either name
export const acceptTrainerInvite = acceptTrainerInviteByCoachId;
export const declineTrainerInvite = declineTrainerInviteByCoachId;

/** Coach lists their active (and pending) client assignments. */
export async function getTrainerRoster(token: string): Promise<TrainerAssignment[]> {
  const resp = await fetchWithAuth<TrainerRosterResponse>(
    `${apiConfig.restApiUrl}api/trainer/roster`,
    token,
  );
  return resp.assignments;
}

/** Client lists pending coach invites directed at them. */
export async function getIncomingTrainerInvites(token: string): Promise<TrainerAssignment[]> {
  const resp = await fetchWithAuth<TrainerIncomingResponse>(
    `${apiConfig.restApiUrl}api/trainer/incoming`,
    token,
  );
  return resp.invites;
}

export interface CoachApplicationRow {
  userId: string;
  email?: string;
  displayName?: string;
  coachApplicationStatus: string;
  coachApplication?: Record<string, unknown>;
}

/** Admin: list pending coach applications (MP 5.8/5.9). */
export async function listCoachApplications(token: string): Promise<CoachApplicationRow[]> {
  const resp = await fetchWithAuth<{ applications: CoachApplicationRow[] }>(
    `${apiConfig.coachApiUrl}api/admin/coach-applications`,
    token,
  );
  return resp.applications;
}

export async function approveCoachApplication(token: string, userId: string): Promise<void> {
  await fetchWithAuth(
    `${apiConfig.coachApiUrl}api/admin/coach-applications/${encodeURIComponent(userId)}/approve`,
    token,
    { method: 'POST' },
  );
}

export async function rejectCoachApplication(token: string, userId: string, reason?: string): Promise<void> {
  await fetchWithAuth(
    `${apiConfig.coachApiUrl}api/admin/coach-applications/${encodeURIComponent(userId)}/reject`,
    token,
    { method: 'POST', body: JSON.stringify({ reason }) },
  );
}

export async function getModelProviderStatus(token: string): Promise<ModelProviderStatus> {
  return fetchWithAuth<ModelProviderStatus>(
    `${apiConfig.coachApiUrl}api/admin/model-provider`,
    token,
  );
}

export async function startChatGPTDeviceAuth(token: string): Promise<ChatGPTDeviceStart> {
  return fetchWithAuth<ChatGPTDeviceStart>(
    `${apiConfig.coachApiUrl}api/admin/model-provider/chatgpt/device/start`,
    token,
    { method: 'POST' },
  );
}

export async function pollChatGPTDeviceAuth(
  token: string,
  attemptId: string,
): Promise<ChatGPTDevicePoll> {
  return fetchWithAuth<ChatGPTDevicePoll>(
    `${apiConfig.coachApiUrl}api/admin/model-provider/chatgpt/device/poll`,
    token,
    { method: 'POST', body: JSON.stringify({ attemptId }) },
  );
}

export async function selectBedrockProvider(token: string): Promise<ModelProviderStatus> {
  return fetchWithAuth<ModelProviderStatus>(
    `${apiConfig.coachApiUrl}api/admin/model-provider/bedrock`,
    token,
    { method: 'POST' },
  );
}

export async function disconnectChatGPTProvider(token: string): Promise<ModelProviderStatus> {
  return fetchWithAuth<ModelProviderStatus>(
    `${apiConfig.coachApiUrl}api/admin/model-provider/chatgpt`,
    token,
    { method: 'DELETE' },
  );
}

// ── Direct messages (MP 5.11/5.13) ──
export async function listDmThreads(token: string): Promise<DmThreadListItem[]> {
  const resp = await fetchWithAuth<{ threads: DmThreadListItem[] }>(
    `${apiConfig.coachApiUrl}api/chats/dm`,
    token,
  );
  return resp.threads;
}

export async function getDmThread(token: string, otherUserId: string): Promise<DmThread> {
  return fetchWithAuth<DmThread>(
    `${apiConfig.coachApiUrl}api/chats/dm/${encodeURIComponent(otherUserId)}`,
    token,
  );
}

export async function sendDmMessage(token: string, otherUserId: string, content: string): Promise<DmMessage> {
  const resp = await fetchWithAuth<{ message: DmMessage }>(
    `${apiConfig.coachApiUrl}api/chats/dm/${encodeURIComponent(otherUserId)}/messages`,
    token,
    { method: 'POST', body: JSON.stringify({ content }) },
  );
  return resp.message;
}

export async function markDmRead(token: string, otherUserId: string): Promise<void> {
  await fetchWithAuth(
    `${apiConfig.coachApiUrl}api/chats/dm/${encodeURIComponent(otherUserId)}/read`,
    token,
    { method: 'POST' },
  );
}

/** Client lists their ACTIVE coaches (MP 5.4). */
export async function getActiveCoaches(token: string): Promise<ActiveCoach[]> {
  const resp = await fetchWithAuth<{ coaches: ActiveCoach[] }>(
    `${apiConfig.restApiUrl}api/trainer/coaches`,
    token,
  );
  return resp.coaches;
}

/** Either party ends the coach relationship (MP 5.3). Pass the assignment PAIR. */
export async function endTrainerAssignment(
  token: string,
  coachId: string,
  clientUserId: string,
  reason?: string,
): Promise<{ endedAt: number }> {
  return fetchWithAuth<{ endedAt: number }>(
    `${apiConfig.restApiUrl}api/trainer/assignments/end`,
    token,
    { method: 'POST', body: JSON.stringify({ coachId, clientUserId, reason }) },
  );
}

/**
 * Coach reads the client data the client has chosen to share. Sections hidden
 * by the client's consent policy come back null; `visibility` says which.
 * Throws with the server's message on 403 when the caller isn't an assigned coach.
 */
export async function getTrainerClientSummary(
  token: string,
  clientUserId: string,
): Promise<TrainerClientSummary> {
  return fetchWithAuth<TrainerClientSummary>(
    `${apiConfig.restApiUrl}api/trainer/clients/${encodeURIComponent(clientUserId)}/summary`,
    token,
  );
}

/** Coach writes/replaces a client's active training plan with attribution. */
export async function coachUpdateClientPlan(
  token: string,
  clientUserId: string,
  plan: TrainingPlan,
  changeSummary: string,
): Promise<{ updatedAt: string }> {
  return fetchWithAuth<{ updatedAt: string }>(
    `${apiConfig.restApiUrl}api/trainer/clients/${encodeURIComponent(clientUserId)}/plan`,
    token,
    {
      method: 'POST',
      body: JSON.stringify({ plan, changeSummary }),
    },
  );
}

/**
 * Returns a display label for the editor of a plan, suitable for the attribution badge.
 * Falls back to a sensible default when fields are missing.
 */
export function getEditorLabel(plan: Pick<BasePlanEnvelope, 'lastEditedByActorType' | 'lastEditedByDisplayName' | 'publishPolicyMode'>): string {
  const { lastEditedByActorType, lastEditedByDisplayName } = plan;
  if (!lastEditedByActorType) return 'AI Coach';
  if (lastEditedByActorType === 'human') return lastEditedByDisplayName || 'Your Coach';
  return lastEditedByDisplayName || 'AI Coach';
}
